// src/products/dataroom/hooks/useGroupModals.js
// Custom hook for managing group modal states and form data

import { useState, useCallback, useEffect } from 'react';

const useGroupModals = () => {
  // Modal visibility states
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showPermissionMatrix, setShowPermissionMatrix] = useState(false);
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null);
  
  // Form and selection states
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  
  // UI states
  const [openMenuId, setOpenMenuId] = useState(null);

  // Effect to close dropdown menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.group-actions-menu')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  // Create Group Modal handlers
  const openCreateGroupModal = useCallback(() => {
    setShowCreateGroup(true);
    setNewGroupName('');
    setNewGroupDescription('');
  }, []);

  const closeCreateGroupModal = useCallback(() => {
    setShowCreateGroup(false);
    setNewGroupName('');
    setNewGroupDescription('');
  }, []);

  // Add User Modal handlers
  const openAddUserModal = useCallback((group) => {
    setSelectedGroup(group);
    setShowAddUser(true);
    setNewUserEmail('');
  }, []);

  const closeAddUserModal = useCallback(() => {
    setShowAddUser(false);
    setSelectedGroup(null);
    setNewUserEmail('');
  }, []);

  // Edit Group handlers
  const startEditGroup = useCallback((group) => {
    setEditingGroup(group.id);
    setNewGroupName(group.name);
    setNewGroupDescription(group.description || '');
    setOpenMenuId(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingGroup(null);
    setNewGroupName('');
    setNewGroupDescription('');
  }, []);

  // Delete Group handlers
  const openDeleteConfirmation = useCallback((groupId) => {
    setConfirmDeleteGroup(groupId);
    setOpenMenuId(null);
  }, []);

  const closeDeleteConfirmation = useCallback(() => {
    setConfirmDeleteGroup(null);
  }, []);

  // Permission Matrix handlers
  const openPermissionMatrix = useCallback(() => {
    setShowPermissionMatrix(true);
  }, []);

  const closePermissionMatrix = useCallback(() => {
    setShowPermissionMatrix(false);
  }, []);

  // Menu handlers
  const toggleMenu = useCallback((groupId) => {
    setOpenMenuId(openMenuId === groupId ? null : groupId);
  }, [openMenuId]);

  const closeMenu = useCallback(() => {
    setOpenMenuId(null);
  }, []);

  // Form data handlers
  const updateGroupName = useCallback((name) => {
    setNewGroupName(name);
  }, []);

  const updateGroupDescription = useCallback((description) => {
    setNewGroupDescription(description);
  }, []);

  const updateUserEmail = useCallback((email) => {
    setNewUserEmail(email);
  }, []);

  // Get current form data
  const getGroupFormData = useCallback(() => ({
    name: newGroupName,
    description: newGroupDescription
  }), [newGroupName, newGroupDescription]);

  const getUserFormData = useCallback(() => ({
    email: newUserEmail
  }), [newUserEmail]);

  return {
    // Modal visibility states
    showCreateGroup,
    showAddUser,
    showPermissionMatrix,
    confirmDeleteGroup,
    
    // Form and selection states
    selectedGroup,
    editingGroup,
    newGroupName,
    newGroupDescription,
    newUserEmail,
    openMenuId,
    
    // Modal handlers
    openCreateGroupModal,
    closeCreateGroupModal,
    openAddUserModal,
    closeAddUserModal,
    openPermissionMatrix,
    closePermissionMatrix,
    
    // Edit handlers
    startEditGroup,
    cancelEdit,
    
    // Delete handlers
    openDeleteConfirmation,
    closeDeleteConfirmation,
    
    // Menu handlers
    toggleMenu,
    closeMenu,
    
    // Form handlers
    updateGroupName,
    updateGroupDescription,
    updateUserEmail,
    getGroupFormData,
    getUserFormData
  };
};

export default useGroupModals;
